import { h, vaciar, m2, num, idAleatorio } from '../dom';
import { anadir } from '../dom';
import type { Contexto } from '../contexto';
import type { Estado } from '../estado';
import { SIN_AGUA_CERCA_M, type Criterios, type Resultado, type ClaveObjetivo } from '../../db/consultas';
import type { BusquedaGuardada } from '../../db/tipos';
import { USOS, nombreUso } from '../../catastro/gml';
import { aUtm } from '../../geo/proyecciones';
import { DIST_MAX_AGUA_M } from '../../ign/hidrografia';

/** Un criterio numerico: valor del anuncio y tolerancia (porcentaje o unidades). */
interface CampoNumerico {
  clave: ClaveObjetivo;
  etiqueta: string;
  unidad: string;
  tolPorcentaje: boolean;
  tolDefecto: number;
}

const CAMPOS: CampoNumerico[] = [
  { clave: 'areaParcela', etiqueta: 'Superficie de parcela', unidad: 'm²', tolPorcentaje: true, tolDefecto: 15 },
  { clave: 'superficieConstruccion', etiqueta: 'Construcción (huella o construida)', unidad: 'm²', tolPorcentaje: true, tolDefecto: 15 },
  { clave: 'supConstruida', etiqueta: 'Superficie construida (total)', unidad: 'm²', tolPorcentaje: true, tolDefecto: 15 },
  { clave: 'huella', etiqueta: 'Huella del edificio', unidad: 'm²', tolPorcentaje: true, tolDefecto: 15 },
  { clave: 'plantas', etiqueta: 'Plantas sobre rasante', unidad: '', tolPorcentaje: false, tolDefecto: 0 },
  { clave: 'anio', etiqueta: 'Año de construcción', unidad: '', tolPorcentaje: false, tolDefecto: 5 },
  { clave: 'viviendas', etiqueta: 'Número de viviendas', unidad: '', tolPorcentaje: false, tolDefecto: 0 },
];

interface Formulario {
  valores: Partial<Record<ClaveObjetivo, number>>;
  tolerancias: Partial<Record<ClaveObjetivo, number>>;
  usos: Set<string>;
  soloConEdificio: boolean;
  soloVista: boolean;
  radioM: number;
  /** "Cerca de un rio o arroyo": distancia maxima al agua (IGN) calculada en la ingesta. */
  cercaAgua: boolean;
  distAguaM: number;
  nombreAgua: string;
  /** Solo parcelas dentro del limite municipal de la zona (si la zona es un municipio). */
  soloMunicipio: boolean;
  /** Municipio (codigo) para la busqueda nacional del servidor; vacio = todos los cargados. */
  municipioNacional: string;
}

const LOTE = 100;

function formularioVacio(): Formulario {
  return { valores: {}, tolerancias: {}, usos: new Set(), soloConEdificio: false, soloVista: false, radioM: 500, cercaAgua: false, distAguaM: 200, nombreAgua: '', soloMunicipio: true, municipioNacional: '' };
}

/** Panel "Buscar": criterios del anuncio → SQL local → resultados resaltados y listados. */
export class BuscarPanel {
  readonly el = h('section', { class: 'panel-contenido' });
  private form: Formulario = formularioVacio();
  private mostrados = LOTE;
  private busquedas: BusquedaGuardada[] = [];
  private listaEl = h('div', { class: 'lista' });
  private cabeceraEl = h('div', { class: 'resultados-cabecera' });

  constructor(private readonly ctx: Contexto) {
    ctx.almacen.suscribir((e, cambios) => {
      if ('resultados' in cambios || 'candidatos' in cambios || 'rcSeleccionado' in cambios) this.renderResultados(e);
      if ('centroBusqueda' in cambios || 'herramienta' in cambios || 'zona' in cambios || 'resumen' in cambios) this.render(e);
    });
    this.render(ctx.almacen.estado);
    void this.cargarBusquedas();
    void import('../../lidar/api').then((m) => m.coberturaNacional()).then((c) => {
      if (c.disponible) { this.nacionalDisponible = true; this.coberturaNacional = c.cobertura ?? []; this.render(this.ctx.almacen.estado); }
    });
  }

  private render(e: Estado): void {
    vaciar(this.el);
    const f = this.form;
    const sinAgua = !e.resumen || e.resumen.cursosAgua === 0;
    const municipio = e.zona?.poligono && e.zona.poligono.length ? e.zona.municipio ?? null : null;
    anadir(this.el,
      h('h2', null, '2. Buscar por los datos del anuncio'),
      h('p', { class: 'ayuda' }, 'Rellena solo lo que da el anuncio. Cada valor admite una tolerancia porque los metros del Catastro rara vez coinciden exactos.'),
      ...CAMPOS.map((c) => this.campo(c)),
      h('h3', null, 'Uso del edificio'),
      h('div', { class: 'chips' }, ...Object.entries(USOS).map(([codigo, nombre]) =>
        h('label', { class: `chip ${f.usos.has(codigo) ? 'activo' : ''}` },
          h('input', { type: 'checkbox', checked: f.usos.has(codigo), onChange: (ev: Event) => { if ((ev.target as HTMLInputElement).checked) f.usos.add(codigo); else f.usos.delete(codigo); this.render(this.ctx.almacen.estado); } }),
          nombre))),
      h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: f.soloConEdificio, onChange: (ev: Event) => { f.soloConEdificio = (ev.target as HTMLInputElement).checked; } }), ' Solo parcelas con edificio'),
      h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: f.soloVista, onChange: (ev: Event) => { f.soloVista = (ev.target as HTMLInputElement).checked; } }), ' Solo dentro de la vista actual'),
      municipio ? h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: f.soloMunicipio, onChange: (ev: Event) => { f.soloMunicipio = (ev.target as HTMLInputElement).checked; } }), ` Solo dentro del término de ${municipio.nombre}`) : null,
      h('h3', null, 'Agua (ríos y arroyos del IGN)'),
      this.bloqueAgua(),
      h('div', { class: 'campo-num' },
        h('label', null, 'Nombre del río o arroyo (opcional; el más cercano a la parcela)'),
        h('input', { type: 'text', placeholder: 'p. ej. Guadiaro', value: f.nombreAgua, 'aria-label': 'Nombre del río', onInput: (ev: Event) => { f.nombreAgua = (ev.target as HTMLInputElement).value; }, onKeydown: (ev: KeyboardEvent) => { if (ev.key === 'Enter') void this.buscar(); } })),
      sinAgua ? h('p', { class: 'silencio' }, 'Para usar estos criterios carga la zona con la capa «Ríos y arroyos» marcada (pestaña Zona).') : null,
      this.nacionalDisponible ? h('div', null,
        h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: this.nacional, onChange: (ev: Event) => { this.nacional = (ev.target as HTMLInputElement).checked; this.render(this.ctx.almacen.estado); } }), ' Buscar en la base nacional del servidor (municipios cargados) en vez de en la zona'),
        this.nacional ? h('label', { class: 'campo' }, 'Municipio ',
          h('select', { onChange: (ev: Event) => { f.municipioNacional = (ev.target as HTMLSelectElement).value; } },
            h('option', { value: '', selected: !f.municipioNacional }, 'Todos los cargados'),
            ...this.coberturaNacional.map((c) => h('option', { value: c.municipio, selected: f.municipioNacional === c.municipio || (!f.municipioNacional && municipio?.codigo === c.municipio) }, `${c.municipio} (${num(c.parcelas)} parcelas)`)))) : null) : null,
      h('div', { class: 'campo-inline' },
        h('button', { class: `boton secundario ${e.herramienta === 'punto' ? 'activo' : ''}`, onClick: () => this.elegirPunto() }, e.centroBusqueda ? 'Cambiar punto' : 'Cerca de un punto…'),
        e.centroBusqueda ? h('span', null, ' a menos de ',
          h('input', { type: 'number', class: 'corto', value: String(f.radioM), min: '50', step: '50', onChange: (ev: Event) => { f.radioM = Number((ev.target as HTMLInputElement).value) || 500; } }),
          ' m ', h('button', { class: 'enlace', onClick: () => { this.ctx.almacen.set({ centroBusqueda: null }); this.ctx.mapa.setPuntos([]); } }, 'quitar')) : null),
      h('div', { class: 'fila-botones' },
        h('button', { class: 'boton primario', onClick: () => void this.buscar() }, 'Buscar'),
        h('button', { class: 'boton secundario', onClick: () => this.limpiar() }, 'Limpiar'),
        h('button', { class: 'boton secundario', onClick: () => void this.guardar() }, 'Guardar búsqueda'),
      ),
      this.busquedas.length ? h('div', { class: 'guardadas' }, h('h3', null, 'Búsquedas guardadas'),
        ...this.busquedas.map((b) => h('div', { class: 'guardada' },
          h('button', { class: 'enlace', onClick: () => this.cargarBusqueda(b) }, b.nombre),
          h('button', { class: 'enlace peligro', title: 'Borrar', onClick: () => void this.borrarBusqueda(b.id) }, '×')))) : null,
      this.cabeceraEl,
      this.listaEl,
    );
    this.renderResultados(e);
  }

  /** "Cerca de un rio": tocar la distancia marca la casilla sin volver a pintar el panel (se perderia lo escrito). */
  private bloqueAgua(): HTMLElement {
    const f = this.form;
    const casilla = h('input', { type: 'checkbox', checked: f.cercaAgua, onChange: (ev: Event) => { f.cercaAgua = (ev.target as HTMLInputElement).checked; } });
    const distancia = h('input', {
      type: 'number', class: 'corto', value: String(f.distAguaM), min: '0', max: String(DIST_MAX_AGUA_M), step: '50', 'aria-label': 'Distancia máxima al agua',
      onInput: (ev: Event) => {
        f.distAguaM = Math.min(DIST_MAX_AGUA_M, Math.max(0, Number((ev.target as HTMLInputElement).value) || 0));
        f.cercaAgua = true;
        casilla.checked = true;
      },
      onKeydown: (ev: KeyboardEvent) => { if (ev.key === 'Enter') void this.buscar(); },
    });
    return h('label', { class: 'check' }, casilla, ' Cerca de un río o arroyo: a menos de ', distancia, ' m');
  }

  private campo(c: CampoNumerico): HTMLElement {
    const f = this.form;
    const tol = f.tolerancias[c.clave] ?? c.tolDefecto;
    return h('div', { class: 'campo-num' },
      h('label', null, c.etiqueta),
      h('div', { class: 'campo-inline' },
        h('input', {
          type: 'number', class: 'medio', placeholder: c.unidad || 'valor', value: f.valores[c.clave] !== undefined ? String(f.valores[c.clave]) : '',
          onInput: (ev: Event) => {
            const v = (ev.target as HTMLInputElement).value;
            if (v === '') delete f.valores[c.clave]; else f.valores[c.clave] = Number(v);
          },
          onKeydown: (ev: KeyboardEvent) => { if (ev.key === 'Enter') void this.buscar(); },
        }),
        h('span', { class: 'silencio' }, ' ± '),
        h('input', {
          type: 'number', class: 'corto', value: String(tol), min: '0',
          onInput: (ev: Event) => { f.tolerancias[c.clave] = Number((ev.target as HTMLInputElement).value) || 0; },
        }),
        h('span', { class: 'silencio' }, c.tolPorcentaje ? ' %' : c.clave === 'anio' ? ' años' : ''),
      ));
  }

  /** Traduce el formulario a criterios: rango = valor ± tolerancia, objetivo = valor. */
  criterios(): Criterios {
    const f = this.form;
    const c: Criterios = { objetivos: {}, limite: 5000 };
    for (const campo of CAMPOS) {
      const v = f.valores[campo.clave];
      if (v === undefined || !Number.isFinite(v)) continue;
      const tol = f.tolerancias[campo.clave] ?? campo.tolDefecto;
      const delta = campo.tolPorcentaje ? Math.abs(v) * tol / 100 : tol;
      const rango = { min: v - delta, max: v + delta };
      c[campo.clave] = rango;
      c.objetivos![campo.clave] = v;
    }
    if (f.usos.size) c.usos = [...f.usos];
    if (f.soloConEdificio) c.conEdificio = true;
    if (f.soloVista) {
      const { bbox, epsg } = this.ctx.mapa.bboxVistaUtm();
      if (epsg === this.epsgDatos()) c.bbox = bbox;
    }
    const centro = this.ctx.almacen.estado.centroBusqueda;
    if (centro) { c.centro = centro; c.radioM = f.radioM; }
    const zona = this.ctx.almacen.estado.zona;
    if (f.soloMunicipio && zona?.poligono && zona.poligono.length && zona.epsg === this.epsgDatos()) c.poligono = zona.poligono;
    if (f.cercaAgua) c.distAguaMax = f.distAguaM;
    if (f.nombreAgua.trim()) c.nombreAgua = f.nombreAgua.trim();
    if (Object.keys(c.objetivos!).length === 0) delete c.objetivos;
    return c;
  }

  private epsgDatos(): number {
    return this.ctx.almacen.estado.resumen?.epsg ?? this.ctx.mapa.epsgVista();
  }

  /** Busqueda a escala de municipio/provincia en el servidor (fase 5), si la API tiene base DuckDB. */
  private nacional = false;
  private nacionalDisponible = false;
  private coberturaNacional: { municipio: string; parcelas: number }[] = [];

  private async buscarEnServidor(): Promise<void> {
    const c = this.criterios();
    const f = this.form;
    const municipio = f.municipioNacional || (this.ctx.almacen.estado.zona?.municipio?.codigo && this.coberturaNacional.some((x) => x.municipio === this.ctx.almacen.estado.zona!.municipio!.codigo) ? this.ctx.almacen.estado.zona!.municipio!.codigo : undefined);
    const p: Record<string, string | number | undefined> = {
      areaMin: c.areaParcela?.min, areaMax: c.areaParcela?.max,
      construidaMin: c.supConstruida?.min ?? c.superficieConstruccion?.min, construidaMax: c.supConstruida?.max ?? c.superficieConstruccion?.max,
      huellaMin: c.huella?.min, huellaMax: c.huella?.max,
      plantasMin: c.plantas?.min, plantasMax: c.plantas?.max, anioMin: c.anio?.min, anioMax: c.anio?.max,
      viviendasMin: c.viviendas?.min, viviendasMax: c.viviendas?.max, usos: c.usos?.join(','),
      bbox: c.bbox ? c.bbox.join(',') : undefined, municipio, limite: 2000,
    };
    const { buscarNacional } = await import('../../lidar/api');
    const filas = await buscarNacional(p);
    const resultados: Resultado[] = filas.map((x) => ({
      rc: x.rc, etiqueta: x.etiqueta, areaM2: x.areaM2, cx: x.cx, cy: x.cy, uso: x.uso, estado: null, anio: x.anio, plantas: x.plantas,
      viviendas: x.viviendas, supConstruidaM2: x.supConstruidaM2, huellaM2: x.huellaM2, nEdificios: x.supConstruidaM2 !== null ? 1 : 0, nPartes: 0,
      fotoUrl: null, ajuste: null, distanciaM: null, distAguaM: null, aguaNombre: null, esCandidato: this.ctx.almacen.estado.candidatos.some((y) => y.rc === x.rc),
    }));
    this.mostrados = LOTE;
    this.ctx.almacen.set({ resultados, panelLateral: 'buscar' });
    this.ctx.mapa.setResultados(resultados.map((r) => r.rc));
    const nota = c.distAguaMax !== undefined || c.nombreAgua ? ' La base nacional no tiene distancias al agua: ese criterio solo se aplica a la zona cargada.' : '';
    this.ctx.almacen.avisar(resultados.length ? `${resultados.length} parcelas en el servidor (${municipio ? 'municipio ' + municipio : 'base nacional'}). Las que no estén en la zona cargada se localizan al pulsarlas.${nota}` : `Ninguna parcela cumple los criterios en la base nacional.${nota}`);
  }

  async buscar(): Promise<void> {
    if (this.nacional) {
      try { await this.buscarEnServidor(); } catch (err) { this.ctx.almacen.avisar(`Búsqueda nacional: ${err instanceof Error ? err.message : String(err)}`, 'error'); }
      return;
    }
    const resumen = this.ctx.almacen.estado.resumen;
    if (!resumen || resumen.parcelas === 0) {
      this.ctx.almacen.avisar('Primero carga una zona en la pestaña Zona.', 'error');
      return;
    }
    if ((this.form.cercaAgua || this.form.nombreAgua.trim()) && resumen.cursosAgua === 0) {
      this.ctx.almacen.avisar('No hay ríos cargados: en la pestaña Zona marca la capa «Ríos y arroyos» y vuelve a cargar la zona.', 'error');
      return;
    }
    try {
      const resultados = await this.ctx.db.buscar(this.criterios());
      this.mostrados = LOTE;
      this.ctx.almacen.set({ resultados, panelLateral: 'buscar' });
      this.ctx.mapa.setResultados(resultados.map((r) => r.rc));
      if (resultados.length === 0) this.ctx.almacen.avisar('Ninguna parcela cumple los criterios. Amplía las tolerancias o quita alguno.');
    } catch (err) {
      this.ctx.almacen.avisar(`Error en la búsqueda: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  }

  private limpiar(): void {
    this.form = formularioVacio();
    this.ctx.almacen.set({ resultados: [], centroBusqueda: null });
    this.ctx.mapa.setResultados([]);
    this.ctx.mapa.setPuntos([]);
    this.render(this.ctx.almacen.estado);
  }

  private elegirPunto(): void {
    const activa = this.ctx.almacen.estado.herramienta === 'punto';
    this.ctx.almacen.set({ herramienta: activa ? 'ninguna' : 'punto' });
    this.ctx.mapa.setHerramienta(activa ? 'ninguna' : 'punto');
    this.ctx.mapa.onPuntoElegido = (lonLat) => {
      const u = aUtm(lonLat[0], lonLat[1], this.epsgDatos());
      this.ctx.almacen.set({ centroBusqueda: [u.x, u.y], herramienta: 'ninguna' });
      this.ctx.mapa.setPuntos([lonLat]);
    };
  }

  private renderResultados(e: Estado): void {
    const res = e.resultados;
    vaciar(this.cabeceraEl);
    vaciar(this.listaEl);
    if (res.length === 0) return;
    const orden = res[0].ajuste !== null ? ' ordenados por parecido' : res[0].distanciaM !== null ? ' ordenados por distancia'
      : (this.form.cercaAgua || this.form.nombreAgua.trim()) && res[0].distAguaM !== null ? ' ordenados por distancia al agua' : '';
    anadir(this.cabeceraEl, h('h3', null, `${num(res.length)} resultado${res.length === 1 ? '' : 's'}`), h('span', { class: 'silencio' }, orden));
    const candidatos = new Set(e.candidatos.map((c) => c.rc));
    for (const r of res.slice(0, this.mostrados)) anadir(this.listaEl, this.itemResultado(r, candidatos.has(r.rc), e.rcSeleccionado === r.rc));
    if (res.length > this.mostrados) {
      anadir(this.listaEl, h('button', { class: 'boton secundario ancho', onClick: () => { this.mostrados += LOTE; this.renderResultados(this.ctx.almacen.estado); } }, `Mostrar ${Math.min(LOTE, res.length - this.mostrados)} más`));
    }
  }

  private itemResultado(r: Resultado, esCandidato: boolean, seleccionado: boolean): HTMLElement {
    const detalles: string[] = [];
    if (r.areaM2 !== null) detalles.push(`parcela ${m2(r.areaM2)}`);
    if (r.supConstruidaM2 !== null) detalles.push(`construida ${m2(r.supConstruidaM2)}`);
    if (r.huellaM2 !== null) detalles.push(`huella ${m2(r.huellaM2)}`);
    if (r.plantas !== null) detalles.push(`${r.plantas} pl.`);
    if (r.anio !== null) detalles.push(String(r.anio));
    if (r.viviendas !== null && r.viviendas > 1) detalles.push(`${r.viviendas} viv.`);
    if (r.distAguaM !== null && r.distAguaM < SIN_AGUA_CERCA_M) detalles.push(`${r.aguaNombre ?? 'arroyo sin nombre'} a ${num(r.distAguaM)} m`);
    return h('div', {
      class: `item ${seleccionado ? 'seleccionado' : ''} ${esCandidato ? 'candidato' : ''}`,
      onClick: () => void this.ctx.seleccionar(r.rc, true),
      onMouseenter: () => this.ctx.mapa.setHover(r.rc),
      onMouseleave: () => this.ctx.mapa.setHover(null),
    },
    h('div', { class: 'item-titulo' },
      h('span', { class: 'rc' }, r.rc),
      r.ajuste !== null ? h('span', { class: 'ajuste', title: 'Desviación acumulada respecto a los valores buscados' }, `${Math.round(r.ajuste * 100)} %`) : null,
      r.distanciaM !== null ? h('span', { class: 'ajuste' }, `${num(r.distanciaM)} m`) : null,
      h('button', { class: `estrella ${esCandidato ? 'activo' : ''}`, title: esCandidato ? 'Quitar de candidatos' : 'Marcar como candidato', onClick: (ev: Event) => { ev.stopPropagation(); void this.ctx.alternarCandidato(r.rc); } }, esCandidato ? '★' : '☆')),
    h('div', { class: 'item-detalle' }, detalles.join(' · ')),
    h('div', { class: 'item-detalle silencio' }, nombreUso(r.uso), r.etiqueta ? ` · nº ${r.etiqueta}` : ''));
  }

  // ---------- Busquedas guardadas ----------

  private async cargarBusquedas(): Promise<void> {
    this.busquedas = await this.ctx.db.busquedas();
    this.render(this.ctx.almacen.estado);
  }

  private async guardar(): Promise<void> {
    const nombre = prompt('Nombre de la búsqueda:');
    if (!nombre) return;
    const f = this.form;
    const serial = {
      valores: f.valores, tolerancias: f.tolerancias, usos: [...f.usos], soloConEdificio: f.soloConEdificio, soloVista: f.soloVista, radioM: f.radioM,
      cercaAgua: f.cercaAgua, distAguaM: f.distAguaM, nombreAgua: f.nombreAgua, soloMunicipio: f.soloMunicipio,
    };
    await this.ctx.db.guardarBusqueda({ id: idAleatorio(), nombre, criterios: JSON.stringify(serial), creada: Date.now() });
    await this.cargarBusquedas();
  }

  private cargarBusqueda(b: BusquedaGuardada): void {
    try {
      const s = JSON.parse(b.criterios) as Partial<{
        valores: Formulario['valores']; tolerancias: Formulario['tolerancias']; usos: string[]; soloConEdificio: boolean; soloVista: boolean; radioM: number;
        cercaAgua: boolean; distAguaM: number; nombreAgua: string; soloMunicipio: boolean;
      }>;
      this.form = {
        ...formularioVacio(),
        valores: s.valores ?? {}, tolerancias: s.tolerancias ?? {}, usos: new Set(s.usos ?? []), soloConEdificio: !!s.soloConEdificio, soloVista: !!s.soloVista, radioM: s.radioM ?? 500,
        cercaAgua: !!s.cercaAgua, distAguaM: s.distAguaM ?? 200, nombreAgua: s.nombreAgua ?? '', soloMunicipio: s.soloMunicipio ?? true,
      };
      this.render(this.ctx.almacen.estado);
      void this.buscar();
    } catch {
      this.ctx.almacen.avisar('La búsqueda guardada no se pudo leer.', 'error');
    }
  }

  private async borrarBusqueda(id: string): Promise<void> {
    await this.ctx.db.borrarBusqueda(id);
    await this.cargarBusquedas();
  }
}
